import { useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ListItemIcon, Menu, MenuItem, Popover, useTheme } from '@mui/material';

import Button from './Button';

interface InnerData {
    label: string;
    value: string;
    id: string;
}

interface NestedMenuProps {
    data: {
        group: string;
        id: string;
        innerData: Array<InnerData>;
    }[];
    label: string;
    config: {
        minWidth: number;
        height: number;
    };
    handleItemClick: (value: string) => void;
}

export const NestedMenu = ({ data, label, config, handleItemClick }: NestedMenuProps) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [submenuAnchorEl, setSubmenuAnchorEl] = useState<null | HTMLElement>(null);
    const [activeGroupId, setActiveGroupId] = useState<string | null>(null);

    const isMenuOpen = Boolean(anchorEl);
    const isSubmenuOpen = Boolean(submenuAnchorEl);

    const submenuContent = activeGroupId
        ? data.find((group) => group.id === activeGroupId)!.innerData
        : null;

    const hasContentForSubmenu = submenuContent && submenuContent.length > 0;

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSubmenuAnchorEl(null);
        setActiveGroupId(null);
    };

    return (
        <>
            <Button
                variant={isMenuOpen ? 'outlined' : 'contained'}
                colorVariant='purple'
                whiteText
                lighterFont
                minWidth={config.minWidth}
                iconType={isMenuOpen ? 'open-eye' : 'close-eye'}
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                {label}
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleCloseMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            ml: theme.spacing(2),
                            boxShadow: theme.customShadows.selectDropdown,
                            '& .MuiMenu-list': {
                                py: 0,
                            },
                        },
                    },
                }}
            >
                {data.map(({ id, group, innerData }) => {
                    const hasOptionsForSubmenu = innerData.length > 0;
                    return (
                        <MenuItem
                            key={id}
                            sx={{
                                pl: 3,
                                pr: 0,
                                display: 'flex',
                                gap: 3,
                            }}
                            onMouseEnter={(e) => {
                                if (hasOptionsForSubmenu) {
                                    setActiveGroupId(id);
                                    setSubmenuAnchorEl(e.currentTarget);
                                } else {
                                    setActiveGroupId(null);
                                    setSubmenuAnchorEl(null);
                                }
                            }}
                        >
                            {group}
                            {hasOptionsForSubmenu && (
                                <ListItemIcon sx={{ marginLeft: 'auto' }}>
                                    <ChevronRightIcon />
                                </ListItemIcon>
                            )}
                        </MenuItem>
                    );
                })}
            </Menu>

            {hasContentForSubmenu && (
                <Popover
                    key={activeGroupId}
                    anchorEl={submenuAnchorEl}
                    open={isMenuOpen && isSubmenuOpen}
                    onClose={handleCloseMenu}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    sx={{ pointerEvents: 'none' }}
                    slotProps={{
                        paper: {
                            sx: {
                                boxShadow: theme.customShadows.selectDropdown,
                                pointerEvents: 'auto',
                            },
                        },
                    }}
                >
                    {submenuContent.map(({ id, label, value }) => (
                        <MenuItem
                            key={id}
                            onClick={() => {
                                handleItemClick(value);
                                handleCloseMenu();
                            }}
                        >
                            {label}
                        </MenuItem>
                    ))}
                </Popover>
            )}
        </>
    );
};
